import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { IconColor, IconModule, IconSize } from '@freelancer/ui/icon';
import { textVariants } from '@freelancer/ui/tailwind-text';
import {
  FontColor,
  FontWeight,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import {
  TagStatusBackgroundColor,
  TagStatusColor,
  TagStatusSize,
} from './tag-status.types';

@Component({
  standalone: true,
  imports: [IconModule],
  selector: 'fl-tag-status',
  template: `
    @if (iconName) {
      <fl-icon
        class="TagStatusIcon mr-xxsmall"
        [color]="IconColor.INHERIT"
        [name]="iconName"
        [size]="IconSize.SMALL"
      ></fl-icon>
    }
    <span [class]="textClasses">
      <ng-content></ng-content>
    </span>
  `,
  styleUrls: ['./tag-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagStatusComponent implements OnInit, OnChanges {
  IconColor = IconColor;
  IconSize = IconSize;

  @HostBinding('attr.data-color')
  @Input()
  color: TagStatusColor = TagStatusColor.DEFAULT;

  @Input() iconName?: string;
  @Input() textTransform = TextTransform.CAPITALIZE;

  @HostBinding('attr.data-size')
  @Input()
  size: TagStatusSize = TagStatusSize.LARGE;
  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: TagStatusSize;
  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: TagStatusSize;
  @HostBinding('attr.data-background-color')
  @Input()
  backgroundColor = TagStatusBackgroundColor.DEFAULT;

  textSize = TextSize.XSMALL;
  textSizeTablet?: TextSize;
  textSizeDesktop?: TextSize;

  textClasses: string;

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('size' in changes && this.size !== undefined) {
      this.textSize = this.getTextSizeFromTagSize(this.size);
    }

    if ('sizeTablet' in changes && this.sizeTablet !== undefined) {
      this.textSizeTablet = this.getTextSizeFromTagSize(this.sizeTablet);
    }

    if ('sizeDesktop' in changes && this.sizeDesktop !== undefined) {
      this.textSizeDesktop = this.getTextSizeFromTagSize(this.sizeDesktop);
    }

    if (
      'size' in changes ||
      'sizeTablet' in changes ||
      'sizeDesktop' in changes ||
      'textTransform' in changes
    ) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.textClasses = textVariants({
      color: FontColor.INHERIT,
      size: this.textSize,
      sizeTablet: this.textSizeTablet,
      sizeDesktop: this.textSizeDesktop,
      transform: this.textTransform,
      weight: FontWeight.BOLD,
    });
  }

  private getTextSizeFromTagSize(tagSize: TagStatusSize): TextSize {
    switch (tagSize) {
      case TagStatusSize.SMALL:
        return TextSize.XXSMALL;
      case TagStatusSize.LARGE:
        return TextSize.XSMALL;
      default:
        return TextSize.XSMALL;
    }
  }
}
